const ROOT_NODE_ID = "__organization_root";

const isValidNumber = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (value === "N/A") {
    return false;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric);
};

const toNumber = (value) => {
  if (!isValidNumber(value)) {
    return null;
  }
  return Number(value);
};

const ensureAggregate = (container, key) => {
  if (!container.has(key)) {
    container.set(key, { sum: 0, count: 0 });
  }
  return container.get(key);
};

const addToAggregate = (aggregate, value) => {
  aggregate.sum += value;
  aggregate.count += 1;
};

const finaliseAggregate = (aggregate) => {
  if (!aggregate || aggregate.count === 0) {
    return null;
  }
  return aggregate.sum / aggregate.count;
};

const finaliseAggregateMap = (mapLike) => {
  const result = {};
  mapLike.forEach((aggregate, key) => {
    const value = finaliseAggregate(aggregate);
    if (value !== null) {
      result[key] = value;
    }
  });
  return result;
};

const normaliseResponses = (rawData) => {
  if (!rawData) {
    return [];
  }
  if (Array.isArray(rawData)) {
    if (rawData.length === 0) {
      return [];
    }
    if (Array.isArray(rawData[0])) {
      return rawData[0];
    }
    return rawData;
  }
  return [];
};

export const computeHierarchyScores = (coreData, categories, rawData) => {
  const responses = normaliseResponses(rawData);

  const factorDefs = [];
  const factorLookup = new Map();

  const dimensionList = coreData?.questions?.dimensions || [];
  dimensionList.forEach((dimension) => {
    const dimensionId = Number(dimension?.id) || 0;
    const dimensionIndex = dimensionId - 1;
    dimension?.factors?.forEach((factor) => {
      const factorId = Number(factor?.id) || 0;
      const factorIndex = factorId - 1;
      const metricId = `factor:${dimensionId}:${factorId}`;

      factorDefs.push({
        id: metricId,
        label: factor?.title || "",
        dimensionId,
        dimensionIndex,
        factorId,
        factorIndex,
        dimensionTitle: dimension?.title || "",
      });

      factorLookup.set(`${dimensionIndex}|${factorIndex}`, metricId);
    });
  });

  const outcomeSource =
    coreData?.outcomeQuestions?.questions ||
    coreData?.outcome_questions?.questions ||
    [];

  const outcomeDefs = outcomeSource.map((outcome) => ({
    id: `outcome:${outcome?.id}`,
    label: outcome?.name || "",
    outcomeId: outcome?.id,
  }));

  const outcomeLookup = new Map(
    outcomeDefs.map((item) => [String(item.outcomeId), item.id])
  );

  const validOptionIds = new Set();
  categories?.forEach((category) => {
    category?.options?.forEach((option) => {
      if (option?.id) {
        validOptionIds.add(String(option.id));
      }
    });
  });

  const nodeAggregates = new Map();
  const baseline = {
    overall: { sum: 0, count: 0 },
    factors: new Map(),
    outcomes: new Map(),
  };

  const ensureNodeAggregate = (optionId) => {
    if (!nodeAggregates.has(optionId)) {
      nodeAggregates.set(optionId, {
        overall: { sum: 0, count: 0 },
        factors: new Map(),
        outcomes: new Map(),
      });
    }
    return nodeAggregates.get(optionId);
  };

  responses.forEach((response) => {
    const categoriesForResponse = Array.isArray(response?.categories)
      ? response.categories
          .map((category) => ({
            categoryId: category?.id,
            optionId: category?.response,
          }))
          .filter(
            (item) =>
              item.optionId !== undefined &&
              item.optionId !== null &&
              validOptionIds.has(String(item.optionId))
          )
      : [];

    if (categoriesForResponse.length === 0) {
      return;
    }

    const questionResponses = Array.isArray(response?.questions)
      ? response.questions.filter((question) => isValidNumber(question?.response))
      : [];

    questionResponses.forEach((question) => {
      const value = toNumber(question?.response);
      if (value === null) {
        return;
      }

      addToAggregate(baseline.overall, value);

      const metricId = factorLookup.get(
        `${Number(question?.id)}|${Number(question?.factor)}`
      );
      if (metricId) {
        const aggregate = ensureAggregate(baseline.factors, metricId);
        addToAggregate(aggregate, value);
      }

      categoriesForResponse.forEach((categoryItem) => {
        const optionAggregate = ensureNodeAggregate(String(categoryItem.optionId));
        addToAggregate(optionAggregate.overall, value);

        if (metricId) {
          const aggregate = ensureAggregate(optionAggregate.factors, metricId);
          addToAggregate(aggregate, value);
        }
      });
    });

    const outcomeResponses = Array.isArray(
      response?.employee_outcomes?.responses
    )
      ? response.employee_outcomes.responses
      : [];

    outcomeResponses.forEach((outcome) => {
      if (outcome?.clicked === false) {
        return;
      }

      const value = toNumber(outcome?.response);
      if (value === null) {
        return;
      }

      const metricId = outcomeLookup.get(String(outcome?.q));
      if (!metricId) {
        return;
      }

      const baselineAggregate = ensureAggregate(baseline.outcomes, metricId);
      addToAggregate(baselineAggregate, value);

      categoriesForResponse.forEach((categoryItem) => {
        const optionAggregate = ensureNodeAggregate(String(categoryItem.optionId));
        const aggregate = ensureAggregate(optionAggregate.outcomes, metricId);
        addToAggregate(aggregate, value);
      });
    });
  });

  const nodeScores = new Map();

  validOptionIds.forEach((optionId) => {
    const aggregates = nodeAggregates.get(optionId);
    if (!aggregates) {
      nodeScores.set(optionId, {
        overall: null,
        factors: {},
        outcomes: {},
      });
      return;
    }

    nodeScores.set(optionId, {
      overall: finaliseAggregate(aggregates.overall),
      factors: finaliseAggregateMap(aggregates.factors),
      outcomes: finaliseAggregateMap(aggregates.outcomes),
    });
  });

  nodeScores.set(ROOT_NODE_ID, {
    overall: finaliseAggregate(baseline.overall),
    factors: finaliseAggregateMap(baseline.factors),
    outcomes: finaliseAggregateMap(baseline.outcomes),
  });

  const factorAvailability = new Map();
  const outcomeAvailability = new Map();

  nodeScores.forEach((node, nodeId) => {
    if (nodeId === ROOT_NODE_ID) {
      return;
    }
    Object.entries(node.factors || {}).forEach(([metricId, value]) => {
      if (value !== null && Number.isFinite(value)) {
        factorAvailability.set(metricId, true);
      }
    });
    Object.entries(node.outcomes || {}).forEach(([metricId, value]) => {
      if (value !== null && Number.isFinite(value)) {
        outcomeAvailability.set(metricId, true);
      }
    });
  });

  const baselineFactors = nodeScores.get(ROOT_NODE_ID)?.factors || {};
  const baselineOutcomes = nodeScores.get(ROOT_NODE_ID)?.outcomes || {};

  const usableFactorDefs = factorDefs.filter((factor) =>
    factorAvailability.get(factor.id) || baselineFactors[factor.id] !== undefined
  );

  const usableOutcomeDefs = outcomeDefs.filter((outcome) =>
    outcomeAvailability.get(outcome.id) || baselineOutcomes[outcome.id] !== undefined
  );

  return {
    nodeScores,
    metrics: {
      factors: usableFactorDefs,
      outcomes: usableOutcomeDefs,
    },
  };
};

export const ROOT_ID = ROOT_NODE_ID;

